import type { Module, PermissionLevel, TenantRole } from './roles';
import { MODULES, TENANT_ROLES } from './roles';

export type PermissionMatrix = Record<TenantRole, Record<Module, PermissionLevel>>;
export type EffectivePermissions = Record<Module, PermissionLevel>;
export type Action = 'read' | 'edit';
export type AccessScope = 'all' | 'own';

type Row = [Module, Record<TenantRole, PermissionLevel>];
const n = 'none' as const;
const o = 'own' as const;
const r = 'read' as const;
const e = 'edit' as const;

// Columns: ADMIN_ECOLE, AGENT_SCOLARITE, AGENT_FINANCE, ENSEIGNANT, ETUDIANT, CANDIDAT
const row = (
  m: Module,
  a: PermissionLevel,
  s: PermissionLevel,
  f: PermissionLevel,
  t: PermissionLevel,
  st: PermissionLevel,
  c: PermissionLevel,
): Row => [
  m,
  { ADMIN_ECOLE: a, AGENT_SCOLARITE: s, AGENT_FINANCE: f, ENSEIGNANT: t, ETUDIANT: st, CANDIDAT: c },
];

/**
 * Platform defaults, copied into each tenant's `role_permission` rows at creation. Mirrors ScrSettings, with
 * narrower defaults: teachers see the students and the curricula of their own groups / programmes (`own`,
 * « Lecture » stays available),
 * finance opens receipts through the finance module rather than every official document.
 */
const DEFAULT_ROWS: Row[] = [
  row('dashboard', e, r, r, n, n, n),
  row('admissions', e, e, n, n, n, o),
  row('students', e, e, r, o, o, n),
  row('curriculum', e, r, n, o, r, n),
  row('timetable', e, e, n, o, o, n),
  row('attendance', e, e, n, o, o, n),
  row('grades', e, r, n, o, o, n),
  row('deliberations', e, r, n, n, n, n),
  // Teachers: internships they supervise or judge; students: their own (declare, deposit, follow).
  row('internships', e, e, n, o, o, n),
  row('documents', e, e, n, n, o, n),
  row('finance', e, r, e, n, o, n),
  row('announcements', e, e, r, r, r, n),
  row('settings', e, n, n, n, n, n),
  row('audit', r, n, n, n, n, n),
];

export function defaultPermissionMatrix(): PermissionMatrix {
  const matrix = {} as PermissionMatrix;
  for (const role of TENANT_ROLES) {
    matrix[role] = {} as Record<Module, PermissionLevel>;
    for (const [module, levels] of DEFAULT_ROWS) matrix[role][module] = levels[role];
  }
  return matrix;
}

const RANK: Record<PermissionLevel, number> = { none: 0, own: 1, read: 2, edit: 3 };

/** Highest level across the user's roles, per module. */
export function effectivePermissions(
  matrix: PermissionMatrix,
  roles: readonly TenantRole[],
): EffectivePermissions {
  const out = {} as EffectivePermissions;
  for (const module of MODULES) {
    let best: PermissionLevel = 'none';
    for (const role of roles) {
      const level = matrix[role][module];
      if (RANK[level] > RANK[best]) best = level;
    }
    out[module] = best;
  }
  return out;
}

/**
 * Returns the scope a user may act with, or null when denied.
 * read: read/edit → all, own → own. edit: edit → all, own → own, read → denied.
 */
export function accessScope(perms: EffectivePermissions, module: Module, action: Action): AccessScope | null {
  const level = perms[module];
  if (level === 'own') return 'own';
  if (action === 'read') return level === 'read' || level === 'edit' ? 'all' : null;
  return level === 'edit' ? 'all' : null;
}

export function can(perms: EffectivePermissions, module: Module, action: Action = 'read'): boolean {
  return accessScope(perms, module, action) !== null;
}

/** Columns of the permission editor (ScrSettings « Rôles & permissions »); candidates keep the platform defaults. */
export const EDITABLE_ROLES = [
  'ADMIN_ECOLE',
  'AGENT_SCOLARITE',
  'AGENT_FINANCE',
  'ENSEIGNANT',
  'ETUDIANT',
] as const;

/**
 * Levels a cell of the editor may take, in click order (empty = locked). The administration column and
 * the settings row are locked (no lock-out, no privilege escalation); `own` cells toggle with « Aucun »;
 * students can only lose or regain their default access (never read other people's data).
 */
export function permissionChoices(role: TenantRole, module: Module): PermissionLevel[] {
  if (role === 'ADMIN_ECOLE' || role === 'CANDIDAT' || module === 'settings') return [];
  const def = defaultPermissionMatrix()[role][module];
  if (module === 'audit') return ['none', 'read'];
  if (role === 'ETUDIANT') return def === 'none' ? [] : [def, 'none'];
  // A teacher's « own » students are those of their groups; the school may open the whole directory.
  if (role === 'ENSEIGNANT' && (module === 'students' || module === 'curriculum'))
    return ['own', 'read', 'none'];
  if (def === 'own') return ['own', 'none'];
  return ['edit', 'read', 'none'];
}
